import * as Sentry from '@sentry/react';
import React, { Suspense, useCallback } from 'react';
import { Provider } from 'react-redux';

import GlobalStore from '../Store/GlobalStore';
import { setIsMobile } from '../Store/Slicers/WindowSlice';
import { importLazyComponent } from '../components/importLazyComponent';
import Loader from '../components/loader/Loader';

import { mountReactNode } from './reactMount';

/**
 * @param lazyComponentPath path to component file without ".lazy.ext" suffix
 * @param idOrClass element ID or class without "component--" prefix, or an array of IDs
 * @param loader ReactNode or null for default loading spinner
 */
function loadComponent(
    lazyComponentPath: string,
    idOrClass: string | string[],
    loader: React.ReactNode | null = undefined
): void {
    const containers = findComponentContainers(idOrClass);

    if (containers.length) {
        const LazyComponent = React.lazy(() => importLazyComponent(lazyComponentPath));
        let loaderElement: React.ReactNode | null = <Loader isLoading elementToShow={null} />;
        if (typeof loader !== 'undefined') {
            loaderElement = loader;
        }

        containers.forEach((containerElement) => {
            const shouldPreserveExistingContent = loader === null && containerElement.innerHTML.trim().length > 0;
            const suspenseFallback = shouldPreserveExistingContent ? (
                <ClonedContentFallback preservedNode={containerElement.cloneNode(true)} />
            ) : (
                <>{loaderElement}</>
            );

            mountReactNode(
                containerElement,
                <Sentry.ErrorBoundary
                    fallback={<ErrorIndicator />}
                    onError={(error, componentStack) => console.error(error, componentStack)}
                >
                    <Suspense fallback={suspenseFallback} name="loadComponent suspense">
                        <Provider store={GlobalStore}>
                            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                            <LazyComponent {...containerElement.dataset} />
                        </Provider>
                    </Suspense>
                </Sentry.ErrorBoundary>
            );
        });
    }
}

function ClonedContentFallback({ preservedNode }: { preservedNode: HTMLElement }) {
    const setHostRef = useCallback(
        (host: HTMLDivElement | null) => {
            host?.replaceChildren(preservedNode.cloneNode(true));
        },
        [preservedNode]
    );

    return <div ref={setHostRef} style={{ display: 'contents' }} />;
}

function ErrorIndicator() {
    return (
        <div className="display-1 text-center text-muted">
            <i className="far fa-times-circle" />
        </div>
    );
}

export default loadComponent;

export function findComponentContainers(idOrClass: string | string[]) {
    let identifiers: string[];
    if (typeof idOrClass === 'string') {
        identifiers = [idOrClass];
    } else {
        identifiers = idOrClass;
    }

    const containers = [];
    identifiers.forEach((identifier) => {
        document
            .querySelectorAll<HTMLElement>(`#${identifier}, #component--${identifier}, .component--${identifier}`)
            .forEach((containerElement) => {
                if (!containerElement) {
                    return;
                }

                containers.push(containerElement);
            });
    });

    return containers;
}

const handleResize = () => {
    GlobalStore.dispatch(setIsMobile(window.innerWidth < 1024));
};

handleResize();
window.addEventListener('resize', handleResize);
